import { useEffect, useRef } from 'react';
import { usePathname } from 'expo-router';
import { useAuthStore, useChatInputStore } from '@/store';
import { useBrowserConnectionStore as store } from '@/store/browserConnection';
import { BROWSER_EXTENSION_ORIGINS, formatBrowserContext } from '@/lib/browserPanel';
import { getChatInputContextIdFromPathname } from '@/lib/chatInputContext';
import { createBrowserWorkClient } from '@/lib/browserPanelWork';
import { fetchWithAuth } from '@/lib/api/client';

const CHANNEL = 'gab-web-browser-v1';
const signedIn = () => {
  const s = useAuthStore.getState();
  return !!(s._hasHydrated && s.token && s.user?.id && !s.user.flags?.isGhost && !s._isSigningOut);
};
export default function BrowserConnectionBridge() {
  const pathname = usePathname(), userId = useAuthStore(s => s.user?.id), authenticated = useAuthStore(s => !!s.token && !s.user?.flags?.isGhost);
  const enabled = store(s => s.enabled);
  const nonce = useRef(null), currentPath = useRef(pathname), client = useRef(null), pending = useRef(new Map());
  currentPath.current = pathname;
  const previous = useRef({ userId, pathname });
  const send = useRef(() => {});

  useEffect(() => {
    if (window.parent !== window) return;
    nonce.current = crypto.randomUUID();
    let active = true;
    const post = (type, extra = {}) => window.postMessage({ channel: CHANNEL, direction: 'page', nonce: nonce.current, type, ...extra }, location.origin);
    send.current = post;
    const request = (type, extra = {}) => new Promise((resolve, reject) => {
      const requestId = crypto.randomUUID();
      const timer = setTimeout(() => { pending.current.delete(requestId); reject(Error('Browser request expired. Inspect before retrying.')); }, 35000);
      pending.current.set(requestId, { resolve: result => { clearTimeout(timer); resolve(result); } });
      post(type, { requestId, ...extra });
    });
    const disconnect = () => {
      client.current?.stop(); client.current = null; post('disconnect');
      store.setState({ enabled: false, picker: false, pages: [], selected: [], status: 'Browser disconnected.' });
    };
    const onMessage = event => {
      const data = event.data;
      if (!active || event.source !== window || event.origin !== location.origin || data?.channel !== CHANNEL || data.direction !== 'extension' || data.nonce !== nonce.current) return;
      if (data.type === 'ready' && BROWSER_EXTENSION_ORIGINS.includes(`chrome-extension://${data.extensionId}`)) store.setState({ available: true });
      if (data.type === 'state') {
        if (!data.enabled) { client.current?.stop(); client.current = null; }
        store.setState({ enabled: data.enabled === true && signedIn(), status: data.message || '', ...(!data.enabled && { picker: false, pages: [], selected: [] }) });
        if (data.enabled && !signedIn()) post('disconnect');
      }
      if (data.type === 'notice') store.setState({ status: String(data.message || '').slice(0, 500) });
      if (data.type === 'reply' && pending.current.has(data.requestId)) { pending.current.get(data.requestId).resolve(data.result); pending.current.delete(data.requestId); }
    };
    window.addEventListener('message', onMessage);
    const hello = () => post('hello'); hello();
    const timer = setInterval(hello, 1500);
    const showError = e => store.setState({ status: e.message || 'Browser connection unavailable.' });
    store.setState({
      connect: () => { if (signedIn()) { post('connect'); store.setState({ status: 'Approve this chat in the Gab AI extension window.' }); } },
      disconnect, options: () => post('options'),
      list: () => void request('tabs').then(result => {
        if (!result?.ok) throw Error(result?.error || 'Could not list browser pages.');
        store.setState({ pages: result.tabs || [], selected: [], picker: true, status: 'Select pages to add to your draft.' });
      }).catch(showError),
      add: () => {
        const owner = useAuthStore.getState().user?.id, sourcePath = currentPath.current;
        return void request('read', { tabIds: store.getState().selected }).then(result => {
        if (!result?.ok) throw Error(result?.error || 'Could not read browser pages.');
        const texts = (result.contexts || []).slice(0, 50).map(formatBrowserContext).filter(Boolean);
        const contextId = getChatInputContextIdFromPathname(currentPath.current);
        if (!contextId || !signedIn() || !store.getState().enabled || owner !== useAuthStore.getState().user?.id || sourcePath !== currentPath.current) return;
        const input = useChatInputStore.getState(), prior = input.inputStates[contextId]?.text || '';
        input.setContext(contextId); input.setText([prior, ...texts].filter(Boolean).join('\n\n'));
        store.setState({ picker: false, status: `Added ${texts.length} ${texts.length === 1 ? 'page' : 'pages'} to your draft. Review before sending.${result.skipped ? ` ${result.skipped} skipped.` : ''}` });
        }).catch(showError);
      },
    });
    return () => { active = false; clearInterval(timer); disconnect(); window.removeEventListener('message', onMessage); store.setState({ available: false }); };
  }, []);

  useEffect(() => {
    if (window.parent !== window) return;
    const prior = previous.current;
    if (!authenticated || !signedIn() || prior.userId !== userId || (/^\/c\//.test(prior.pathname) && prior.pathname !== pathname)) store.getState().disconnect();
    previous.current = { userId, pathname };
  }, [pathname, userId, authenticated]);

  useEffect(() => {
    if (window.parent !== window || !enabled || !store.getState().enabled || !signedIn()) return;
    const conversationId = pathname.match(/^\/c\/([a-f\d]{24})$/)?.[1];
    if (!conversationId) { store.setState({ status: 'Browser connected. Choose Chat or Work and send your task.' }); return; }
    let active = true;
    const requestCommand = command => {
      const requestId = crypto.randomUUID();
      const timer = setTimeout(() => {
        pending.current.delete(requestId);
        void next.result({ connectionId: command.connectionId, id: command.id, result: { ok: false, error: 'Browser response timed out. Inspect before retrying.' } });
      }, 28000);
      pending.current.set(requestId, { resolve: result => { clearTimeout(timer); void next.result({ connectionId: command.connectionId, id: command.id, result }); } });
      send.current('command', { requestId, command });
    };
    const next = createBrowserWorkClient({ request: fetchWithAuth, conversationId,
      current: () => active && signedIn() && useAuthStore.getState().user?.id === userId && store.getState().enabled,
      send: (type, data) => {
        if (type === 'work-connected') { send.current('bind', { connectionId: data.id, conversationId }); store.setState({ status: 'Browser connected to this chat. Websites require approval.' }); }
        if (type === 'work-command') requestCommand(data.command);
        if (type === 'work-error') { store.getState().disconnect(); store.setState({ status: data.message }); }
      } });
    client.current = next; void next.start();
    return () => { active = false; next.stop(); if (client.current === next) client.current = null; };
  }, [pathname, userId, enabled]);
  return null;
}
